import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { PencilSimple, Trash, Plus, FileXls, Clock, CaretDown, CaretRight, X } from "@phosphor-icons/react";

const EMPTY_FORM = { athlete_id: "", record_date: "", race_number: "", event_number: "", time_text: "", note: "" };
const CLASS_OPTIONS = ["Tim Hijau (Pemula)", "Tim Kuning", "Tim Jingga", "Tim Biru", "Tim Pink", "Tim Merah", "Speed", "Slalom"];

function todayInputDate() {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    return new Date(now.getTime() - offset * 60000).toISOString().slice(0, 10);
}

function formatSessionDate(value) {
    if (!value) return "—";
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export default function TimeRecordsTab() {
    const [records, setRecords] = useState([]);
    const [athletes, setAthletes] = useState([]);
    const [filterAthlete, setFilterAthlete] = useState("");
    const [dialog, setDialog] = useState("");
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [batchForm, setBatchForm] = useState({ record_date: todayInputDate(), race_number: "", event_number: "", class_name: "" });
    const [batchEntries, setBatchEntries] = useState({});
    const [saving, setSaving] = useState(false);
    const [raceOptions, setRaceOptions] = useState([]);
    const [raceTemplates, setRaceTemplates] = useState([]);
    const [raceSuggestions, setRaceSuggestions] = useState([]);
    const [mergeChoices, setMergeChoices] = useState({});
    const [mergingRace, setMergingRace] = useState("");
    const [newTemplate, setNewTemplate] = useState("");
    const [sessionDialog, setSessionDialog] = useState(null);
    const [sessionForm, setSessionForm] = useState({ record_date: "", event_number: "", race_number: "" });
    const [sessionRows, setSessionRows] = useState({});
    const [sessionAddClass, setSessionAddClass] = useState("");
    const [sessionAddEntries, setSessionAddEntries] = useState({});

    const loadRaceMeta = () => {
        api.get("/time-records/race-options")
            .then((res) => {
                setRaceTemplates(res.data.templates || []);
                setRaceOptions(res.data.options || []);
            })
            .catch(() => {});
        api.get("/time-records/race-suggestions")
            .then((res) => setRaceSuggestions(res.data))
            .catch(() => {});
    };

    const load = () => {
        Promise.all([api.get("/coach/athletes"), api.get("/time-records")])
            .then(([athletesRes, recordsRes]) => {
                setAthletes(athletesRes.data);
                setRecords(recordsRes.data);
            })
            .catch(() => toast.error("Gagal memuat catatan waktu"));
        loadRaceMeta();
    };

    useEffect(() => {
        load();
    }, []);

    const classOptions = useMemo(() => {
        const fromData = athletes.map((athlete) => athlete.class_name).filter(Boolean);
        return [...new Set([...CLASS_OPTIONS, ...fromData])];
    }, [athletes]);

    const classAthletes = useMemo(() => {
        if (!batchForm.class_name) return [];
        return athletes.filter((athlete) => athlete.class_name === batchForm.class_name);
    }, [athletes, batchForm.class_name]);

    const openCreate = () => {
        setEditing(null);
        setBatchForm({ record_date: todayInputDate(), race_number: "", event_number: "", class_name: "" });
        setBatchEntries({});
        setDialog("create");
    };

    const openEdit = (record) => {
        setEditing(record);
        setForm({
            athlete_id: record.athlete_id,
            record_date: record.record_date,
            race_number: record.race_number || "",
            event_number: record.event_number,
            time_text: record.time_text,
            note: record.note || "",
        });
        setDialog("edit");
    };

    const updateBatchEntry = (athleteId, field, value) => {
        setBatchEntries((prev) => ({
            ...prev,
            [athleteId]: { time_text: "", note: "", ...(prev[athleteId] || {}), [field]: value },
        }));
    };

    const saveBatch = async (e) => {
        e.preventDefault();
        const entries = classAthletes
            .map((athlete) => ({
                athlete_id: athlete.id,
                time_text: (batchEntries[athlete.id]?.time_text || "").trim(),
                note: (batchEntries[athlete.id]?.note || "").trim(),
            }))
            .filter((entry) => entry.time_text);
        if (entries.length === 0) {
            toast.error("Isi waktu minimal satu anggota kelas");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/time-records/bulk", { ...batchForm, entries });
            toast.success(data.message || `${entries.length} catatan waktu tersimpan`);
            setDialog("");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put(`/time-records/${editing.id}`, form);
            toast.success("Catatan waktu tersimpan");
            setDialog("");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (record) => {
        if (!window.confirm(`Hapus catatan waktu ${record.athlete_name} (${record.event_number} • ${record.time_text})?`)) return;
        try {
            await api.delete(`/time-records/${record.id}`);
            toast.success("Catatan waktu dihapus");
            load();
        } catch {
            toast.error("Gagal menghapus catatan waktu");
        }
    };

    const addTemplate = async (e) => {
        e.preventDefault();
        const name = newTemplate.trim();
        if (!name) return;
        try {
            await api.post("/time-records/race-templates", { name });
            toast.success(`"${name}" ditambahkan ke pilihan nomor lomba`);
            setNewTemplate("");
            loadRaceMeta();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const removeTemplate = async (template) => {
        if (!window.confirm(`Hapus "${template.name}" dari daftar pilihan? Catatan waktu yang sudah ada tidak berubah.`)) return;
        try {
            await api.delete(`/time-records/race-templates/${template.id}`);
            toast.success("Nomor lomba dihapus dari daftar");
            loadRaceMeta();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const openSessionEdit = (group) => {
        setSessionDialog(group);
        setSessionForm({ record_date: group.record_date, event_number: group.event_number, race_number: group.race_number });
        const rows = {};
        group.records.forEach((r) => {
            rows[r.athlete_id] = { record_id: r.id, name: r.athlete_name, class_name: r.class_name || "", time_text: r.time_text, note: r.note || "" };
        });
        setSessionRows(rows);
        setSessionAddClass("");
        setSessionAddEntries({});
    };

    const sessionRowList = useMemo(() => {
        if (!sessionDialog) return [];
        return Object.entries(sessionRows)
            .map(([athleteId, row]) => ({ athleteId, ...row }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [sessionRows, sessionDialog]);

    const sessionAddAthletes = useMemo(() => {
        if (!sessionAddClass || !sessionDialog) return [];
        return athletes.filter((a) => a.class_name === sessionAddClass && !sessionRows[a.id]);
    }, [athletes, sessionAddClass, sessionDialog, sessionRows]);

    const updateSessionRow = (athleteId, field, value) => {
        setSessionRows((prev) => ({ ...prev, [athleteId]: { ...prev[athleteId], [field]: value } }));
    };

    const updateSessionAddEntry = (athleteId, field, value) => {
        setSessionAddEntries((prev) => ({
            ...prev,
            [athleteId]: { time_text: "", note: "", ...(prev[athleteId] || {}), [field]: value },
        }));
    };

    const saveSession = async (e) => {
        e.preventDefault();
        if (!sessionDialog) return;
        const sessionChanged =
            sessionForm.record_date !== sessionDialog.record_date ||
            sessionForm.event_number.trim() !== sessionDialog.event_number ||
            sessionForm.race_number.trim() !== sessionDialog.race_number;
        const original = Object.fromEntries(sessionDialog.records.map((r) => [r.athlete_id, r]));
        const edits = [];
        for (const row of sessionRowList) {
            const time = row.time_text.trim();
            if (!time) {
                toast.error(`Waktu ${row.name} tidak boleh kosong — gunakan ikon hapus di tabel bila ingin menghapusnya`);
                return;
            }
            const orig = original[row.athleteId];
            if (orig && (time !== orig.time_text || (row.note || "").trim() !== (orig.note || "").trim())) {
                edits.push(row);
            }
        }
        const additions = athletes
            .filter((a) => sessionAddEntries[a.id] && sessionAddEntries[a.id].time_text.trim() && !sessionRows[a.id])
            .map((a) => ({ athleteId: a.id, time_text: sessionAddEntries[a.id].time_text.trim(), note: (sessionAddEntries[a.id].note || "").trim() }));
        if (!sessionChanged && edits.length === 0 && additions.length === 0) {
            toast.error("Tidak ada perubahan untuk disimpan");
            return;
        }
        if (additions.length > 0 && !sessionForm.race_number.trim()) {
            toast.error("Isi nomor lomba dulu untuk menambahkan atlet ke sesi ini");
            return;
        }
        setSaving(true);
        try {
            if (sessionChanged) {
                await api.put("/time-records/session", {
                    match_date: sessionDialog.record_date,
                    match_event: sessionDialog.event_number,
                    match_race: sessionDialog.race_number,
                    record_date: sessionForm.record_date,
                    event_number: sessionForm.event_number,
                    race_number: sessionForm.race_number,
                });
            }
            for (const row of edits) {
                await api.put(`/time-records/${row.record_id}`, {
                    athlete_id: row.athleteId,
                    record_date: sessionForm.record_date,
                    event_number: sessionForm.event_number,
                    race_number: sessionForm.race_number,
                    time_text: row.time_text.trim(),
                    note: row.note.trim(),
                });
            }
            for (const row of additions) {
                await api.post("/time-records", {
                    athlete_id: row.athleteId,
                    record_date: sessionForm.record_date,
                    event_number: sessionForm.event_number,
                    race_number: sessionForm.race_number,
                    time_text: row.time_text.trim(),
                    note: row.note.trim(),
                });
            }
            const parts = [];
            if (sessionChanged) parts.push("sesi diperbarui");
            if (edits.length) parts.push(`${edits.length} waktu diperbarui`);
            if (additions.length) parts.push(`${additions.length} atlet ditambahkan`);
            toast.success(`Tersimpan: ${parts.join(", ")}`);
            setSessionDialog(null);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const mergeRaces = async (suggestion) => {        const target = mergeChoices[suggestion.id] || suggestion.target_race;
        const source = target === suggestion.source_race ? suggestion.target_race : suggestion.source_race;
        if (!window.confirm(`Gabungkan semua catatan "${source}" menjadi "${target}" untuk SEMUA atlet?`)) return;
        setMergingRace(suggestion.id);
        try {
            const { data } = await api.post("/time-records/merge-race-numbers", { source_race: source, target_race: target });
            toast.success(data.message || "Nomor lomba digabung");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setMergingRace("");
        }
    };

    const visible = filterAthlete ? records.filter((r) => r.athlete_id === filterAthlete) : records;

    const [openSessions, setOpenSessions] = useState({});

    const sessions = useMemo(() => {
        const map = new Map();
        visible.forEach((r) => {
            const key = `${r.record_date}||${r.event_number}||${r.race_number || ""}`;
            if (!map.has(key)) {
                map.set(key, { key, record_date: r.record_date, event_number: r.event_number, race_number: r.race_number || "", records: [] });
            }
            map.get(key).records.push(r);
        });
        const list = [...map.values()];
        list.forEach((group) => {
            group.records.sort((a, b) => (a.time_ms || 0) - (b.time_ms || 0));
            group.best = group.records[0] || null;
        });
        list.sort((a, b) => (b.record_date || "").localeCompare(a.record_date || "") || (a.event_number || "").localeCompare(b.event_number || ""));
        return list;
    }, [visible]);

    const toggleSession = (key) => setOpenSessions((prev) => ({ ...prev, [key]: !prev[key] }));

    return (
        <div data-testid="time-records-tab">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <h2 className="font-display uppercase text-2xl text-neutral-900">Catatan Waktu</h2>
                <div className="flex flex-wrap items-center gap-3">
                    <select
                        value={filterAthlete}
                        onChange={(e) => setFilterAthlete(e.target.value)}
                        data-testid="time-records-filter-athlete"
                        className="bg-white border border-racing-line px-3 py-3 text-sm"
                    >
                        <option value="">Semua Atlet</option>
                        {athletes.map((a) => (
                            <option key={a.id} value={a.id}>{`${a.name} — ${a.class_name || "Tanpa Kelas"}`}</option>
                        ))}
                    </select>
                    <a
                        href={`${api.defaults.baseURL}/time-records/export?format=xlsx`}
                        data-testid="time-records-export-xlsx"
                        className="flex items-center gap-2 border border-racing-line px-4 py-3 text-xs uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red hover:border-racing-red transition-colors"
                    >
                        <FileXls size={16} /> Export Excel
                    </a>
                    <button
                        onClick={openCreate}
                        data-testid="time-records-add-button"
                        className="flex items-center gap-2 bg-racing-red hover:bg-racing-redhover text-white text-xs uppercase tracking-[0.2em] px-5 py-3 transition-colors duration-300"
                    >
                        <Plus size={16} weight="bold" /> Tambah
                    </button>
                </div>
            </div>

            <p className="text-xs text-racing-muted mb-4">Catatan dikelompokkan per hari pengambilan waktu — klik satu baris untuk melihat waktu semua atlet di hari itu. Semakin kecil waktu, semakin baik. Waktu terbaik setiap atlet per judul kegiatan ditandai badge emas.</p>

            <section className="mb-6 border border-racing-line bg-white" data-testid="race-template-manager">
                <div className="px-5 py-4 border-b border-racing-line">
                    <h3 className="font-display uppercase text-lg">Pilihan Nomor Lomba</h3>
                    <p className="text-xs text-racing-muted mt-1">Daftar ini muncul sebagai saran otomatis saat pelatih mengisi nomor lomba. Pelatih dan admin bisa mengubahnya.</p>
                </div>
                <div className="p-5 flex flex-wrap items-center gap-2" data-testid="race-template-list">
                    {raceTemplates.map((template, i) => (
                        <span key={template.id} className="inline-flex items-center gap-2 border border-racing-line bg-racing-surface px-3 py-1.5 text-sm" data-testid={`race-template-${i}`}>
                            {template.name}
                            <button type="button" onClick={() => removeTemplate(template)} data-testid={`race-template-delete-${i}`} className="text-racing-muted hover:text-racing-red transition-colors" aria-label={`Hapus ${template.name}`}>
                                <X size={13} weight="bold" />
                            </button>
                        </span>
                    ))}
                    <form onSubmit={addTemplate} className="flex items-center gap-2">
                        <input
                            value={newTemplate}
                            onChange={(e) => setNewTemplate(e.target.value)}
                            placeholder="Tambah nomor lomba..."
                            data-testid="race-template-input"
                            className="bg-racing-surface border border-racing-line px-3 py-1.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                        />
                        <button type="submit" data-testid="race-template-add" className="border border-racing-red text-racing-red px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] hover:bg-racing-red hover:text-white transition-colors">
                            Tambah
                        </button>
                    </form>
                </div>
                {raceSuggestions.length > 0 && (
                    <div className="border-t border-racing-line" data-testid="race-suggestions-list">
                        <p className="px-5 pt-4 text-[10px] uppercase tracking-[0.2em] text-racing-golddeep">Nomor lomba yang tampak sama — gabungkan agar rekap dan grafik tidak terpecah</p>
                        {raceSuggestions.map((suggestion, i) => (
                            <div key={suggestion.id} className="px-5 py-4 flex flex-col md:flex-row md:items-center gap-3 border-b border-racing-line last:border-0" data-testid={`race-suggestion-${i}`}>
                                <div className="flex-1 min-w-0 text-sm">
                                    <span className="font-medium text-neutral-900">{suggestion.source_race}</span>
                                    <span className="text-racing-muted"> ({suggestion.source_count} catatan) ≈ </span>
                                    <span className="font-medium text-neutral-900">{suggestion.target_race}</span>
                                    <span className="text-racing-muted"> ({suggestion.target_count} catatan) • mirip {Math.round(suggestion.similarity * 100)}%</span>
                                </div>
                                <select
                                    value={mergeChoices[suggestion.id] || suggestion.target_race}
                                    onChange={(e) => setMergeChoices({ ...mergeChoices, [suggestion.id]: e.target.value })}
                                    data-testid={`race-merge-target-${i}`}
                                    className="bg-racing-surface border border-racing-line px-3 py-2 text-sm focus:outline-none focus:border-racing-red"
                                >
                                    <option value={suggestion.target_race}>Gabung ke: {suggestion.target_race}</option>
                                    <option value={suggestion.source_race}>Gabung ke: {suggestion.source_race}</option>
                                </select>
                                <button
                                    type="button"
                                    onClick={() => mergeRaces(suggestion)}
                                    disabled={mergingRace === suggestion.id}
                                    data-testid={`race-merge-button-${i}`}
                                    className="bg-racing-red text-white px-4 py-2 text-[10px] uppercase tracking-[0.15em] hover:bg-racing-redhover disabled:opacity-50 transition-colors"
                                >
                                    {mergingRace === suggestion.id ? "Menggabung..." : "Gabungkan"}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <div className="space-y-4" data-testid="time-records-sessions">
                {sessions.length === 0 && (
                    <p className="border border-racing-line bg-white px-4 py-8 text-center text-racing-muted" data-testid="time-records-empty">
                        Belum ada catatan waktu.
                    </p>
                )}
                {sessions.map((group, i) => {
                    const isOpen = openSessions[group.key] ?? Boolean(filterAthlete);
                    return (
                        <div key={group.key} className="border border-racing-line bg-white" data-testid={`time-record-session-${i}`}>
                            <div className="flex items-stretch">
                                <button
                                    type="button"
                                    onClick={() => toggleSession(group.key)}
                                    data-testid={`time-record-session-toggle-${i}`}
                                    className="flex-1 min-w-0 flex flex-wrap items-center gap-x-6 gap-y-2 px-5 py-4 text-left hover:bg-black/[0.03] transition-colors"
                                >
                                <span className="text-racing-muted">{isOpen ? <CaretDown size={18} /> : <CaretRight size={18} />}</span>
                                <span className="min-w-[190px]">
                                    <span className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted">Tanggal</span>
                                    <span className="block font-medium text-neutral-900" data-testid={`time-record-session-date-${i}`}>{formatSessionDate(group.record_date)}</span>
                                </span>
                                <span className="flex-1 min-w-[200px]">
                                    <span className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted">Kegiatan</span>
                                    <span className="block font-medium text-neutral-900">{group.event_number}{group.race_number ? ` • ${group.race_number}` : ""}</span>
                                </span>
                                <span>
                                    <span className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted">Atlet</span>
                                    <span className="block font-display text-xl text-neutral-900" data-testid={`time-record-session-count-${i}`}>{group.records.length}</span>
                                </span>
                                {group.best && (
                                    <span>
                                        <span className="block text-[10px] uppercase tracking-[0.2em] text-racing-golddeep">Waktu Terbaik</span>
                                        <span className="block font-display text-xl text-racing-golddeep" data-testid={`time-record-session-best-${i}`}>{group.best.time_text}</span>
                                    </span>
                                )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => openSessionEdit(group)}
                                    data-testid={`time-record-session-edit-${i}`}
                                    className="px-4 border-l border-racing-line text-racing-muted hover:text-racing-red hover:bg-racing-red/5 transition-colors"
                                    aria-label="Edit sesi pencatatan"
                                    title="Edit sesi: tanggal, judul kegiatan, nomor lomba, tambah atlet"
                                >
                                    <PencilSimple size={16} />
                                </button>
                            </div>
                            {isOpen && (
                                <div className="border-t border-racing-line overflow-x-auto" data-testid={`time-record-session-detail-${i}`}>
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-racing-line bg-racing-surface">
                                                {["Atlet", "Waktu", "Catatan", ""].map((h) => (
                                                    <th key={h} className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-racing-muted font-medium">{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.records.map((r, j) => (
                                                <tr key={r.id} className="border-b border-racing-line last:border-0 hover:bg-black/[0.03]" data-testid={`time-record-row-${i}-${j}`}>
                                                    <td className="px-4 py-3">
                                                        <p className="font-medium text-neutral-900">{r.athlete_name}</p>
                                                        <p className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">{r.class_name || "—"}</p>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="font-display text-xl text-neutral-900" data-testid={`time-record-value-${i}-${j}`}>{r.time_text}</span>
                                                        {r.is_best && (
                                                            <span className="ml-2 px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] border border-racing-gold text-racing-golddeep bg-racing-gold/10" data-testid={`time-record-best-${i}-${j}`}>
                                                                Terbaik
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-racing-muted max-w-[200px] truncate">{r.note || "—"}</td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex gap-2 justify-end">
                                                            <button onClick={() => openEdit(r)} data-testid={`time-record-edit-${i}-${j}`} className="p-2 border border-racing-line text-racing-muted hover:text-neutral-900 hover:border-black/40 transition-colors" aria-label="Edit">
                                                                <PencilSimple size={16} />
                                                            </button>
                                                            <button onClick={() => remove(r)} data-testid={`time-record-delete-${i}-${j}`} className="p-2 border border-racing-line text-racing-muted hover:text-racing-red hover:border-racing-red transition-colors" aria-label="Hapus">
                                                                <Trash size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <Dialog open={dialog === "create"} onOpenChange={(open) => { if (!open) setDialog(""); }}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="time-record-batch-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase tracking-wide text-xl">Tambah Catatan Waktu per Kelas</DialogTitle>
                        <DialogDescription className="sr-only">Formulir input catatan waktu untuk semua anggota satu kelas.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={saveBatch} className="space-y-5 mt-2" data-testid="time-record-batch-form">
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">1. Tanggal</label>
                                <input
                                    type="date"
                                    required
                                    value={batchForm.record_date}
                                    onChange={(e) => setBatchForm({ ...batchForm, record_date: e.target.value })}
                                    data-testid="time-record-batch-date"
                                    className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">2. Nomor Lomba</label>
                                <select
                                    required
                                    value={batchForm.race_number}
                                    onChange={(e) => setBatchForm({ ...batchForm, race_number: e.target.value })}
                                    data-testid="time-record-batch-race-number"
                                    className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                >
                                    <option value="" disabled>Pilih nomor lomba...</option>
                                    {raceOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">3. Judul Kegiatan</label>
                                <input
                                    type="text"
                                    required
                                    value={batchForm.event_number}
                                    onChange={(e) => setBatchForm({ ...batchForm, event_number: e.target.value })}
                                    placeholder="Contoh: Pengambilan Catatan Waktu Berkala / Kejuaraan Porprov"
                                    data-testid="time-record-batch-event"
                                    className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">4. Pilih Kelas</label>
                                <select
                                    required
                                    value={batchForm.class_name}
                                    onChange={(e) => { setBatchForm({ ...batchForm, class_name: e.target.value }); setBatchEntries({}); }}
                                    data-testid="time-record-batch-class"
                                    className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                >
                                    <option value="" disabled>Pilih kelas</option>
                                    {classOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="border border-racing-gold bg-racing-gold/10 p-4" data-testid="time-record-format-guide">
                            <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-racing-golddeep mb-2"><Clock size={15} /> Cara Mengisi Waktu</p>
                            <div className="grid md:grid-cols-3 gap-3 text-sm text-neutral-800">
                                <p><strong>45,32</strong> = 45 detik 32/100 detik</p>
                                <p><strong>02:45.32</strong> = 2 menit 45,32 detik</p>
                                <p><strong>01:02:45.32</strong> = 1 jam 2 menit 45,32 detik</p>
                            </div>
                            <p className="mt-2 text-xs text-racing-muted">Boleh memakai koma atau titik untuk pecahan detik. Sistem akan merapikan formatnya otomatis.</p>
                        </div>

                        {!batchForm.class_name ? (
                            <p className="border border-racing-line bg-racing-surface p-5 text-sm text-racing-muted" data-testid="time-record-batch-empty-class">Pilih kelas untuk menampilkan daftar anggotanya.</p>
                        ) : classAthletes.length === 0 ? (
                            <p className="border border-racing-line bg-racing-surface p-5 text-sm text-racing-muted" data-testid="time-record-batch-no-athletes">Tidak ada atlet aktif pada kelas ini.</p>
                        ) : (
                            <div className="border border-racing-line" data-testid="time-record-batch-athletes">
                                <div className="grid md:grid-cols-[1fr_180px_1fr] gap-3 px-4 py-3 border-b border-racing-line bg-racing-surface text-[10px] uppercase tracking-[0.2em] text-racing-muted">
                                    <span>Anggota Kelas</span>
                                    <span>Waktu</span>
                                    <span>Catatan Opsional</span>
                                </div>
                                {classAthletes.map((athlete, index) => (
                                    <div key={athlete.id} className="grid md:grid-cols-[1fr_180px_1fr] gap-3 items-center px-4 py-3 border-b border-racing-line last:border-0" data-testid={`time-record-batch-athlete-${index}`}>
                                        <div>
                                            <p className="font-medium text-sm text-neutral-900">{athlete.name}</p>
                                            <p className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">{athlete.nickname || "—"}</p>
                                        </div>
                                        <input
                                            type="text"
                                            value={batchEntries[athlete.id]?.time_text || ""}
                                            onChange={(e) => updateBatchEntry(athlete.id, "time_text", e.target.value)}
                                            placeholder="00:45.32"
                                            data-testid={`time-record-batch-time-${index}`}
                                            className="bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red"
                                        />
                                        <input
                                            type="text"
                                            value={batchEntries[athlete.id]?.note || ""}
                                            onChange={(e) => updateBatchEntry(athlete.id, "note", e.target.value)}
                                            placeholder="Catatan lintasan/cuaca"
                                            data-testid={`time-record-batch-note-${index}`}
                                            className="bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red"
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        <button disabled={saving || !batchForm.class_name} data-testid="time-record-batch-save" className="w-full bg-racing-red hover:bg-racing-redhover disabled:opacity-50 text-white text-xs uppercase tracking-[0.25em] py-3.5 transition-colors duration-300">
                            {saving ? "Menyimpan..." : "Simpan Semua Waktu"}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={dialog === "edit"} onOpenChange={(open) => { if (!open) setDialog(""); }}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-lg" data-testid="time-record-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase tracking-wide text-xl">Edit Catatan Waktu</DialogTitle>
                        <DialogDescription className="sr-only">Formulir untuk memperbarui satu catatan waktu atlet.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={saveEdit} className="space-y-4 mt-2" data-testid="time-record-form">
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Atlet</label>
                            <select
                                required
                                disabled
                                value={form.athlete_id}
                                onChange={(e) => setForm({ ...form, athlete_id: e.target.value })}
                                data-testid="time-record-field-athlete"
                                className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors disabled:opacity-60"
                            >
                                <option value="" disabled>Pilih atlet</option>
                                {athletes.map((a) => (
                                    <option key={a.id} value={a.id}>{`${a.name} — ${a.class_name || "Tanpa Kelas"}`}</option>
                                ))}
                            </select>
                        </div>
                        <div className="grid sm:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Tanggal</label>
                                <input
                                    type="date"
                                    required
                                    value={form.record_date}
                                    onChange={(e) => setForm({ ...form, record_date: e.target.value })}
                                    data-testid="time-record-field-date"
                                    className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nomor Lomba</label>
                                <select
                                    required
                                    value={form.race_number}
                                    onChange={(e) => setForm({ ...form, race_number: e.target.value })}
                                    data-testid="time-record-field-race-number"
                                    className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                >
                                    <option value="" disabled>Pilih nomor lomba...</option>
                                    {raceOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Waktu (Stopwatch)</label>
                                <input
                                    type="text"
                                    required
                                    value={form.time_text}
                                    onChange={(e) => setForm({ ...form, time_text: e.target.value })}
                                    placeholder="00:32.45"
                                    data-testid="time-record-field-time"
                                    className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                />
                                <p className="mt-1 text-[10px] text-racing-muted">Bisa 45,32; 02:45.32; atau 01:02:45.32</p>
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Judul Kegiatan</label>
                            <input
                                type="text"
                                required
                                value={form.event_number}
                                onChange={(e) => setForm({ ...form, event_number: e.target.value })}
                                placeholder="Contoh: Pengambilan Catatan Waktu Berkala / Kejuaraan Porprov"
                                data-testid="time-record-field-event"
                                className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Catatan (opsional)</label>
                            <input
                                type="text"
                                value={form.note}
                                onChange={(e) => setForm({ ...form, note: e.target.value })}
                                data-testid="time-record-field-note"
                                className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={saving}
                            data-testid="time-record-save-button"
                            className="w-full bg-racing-red hover:bg-racing-redhover disabled:opacity-50 text-white text-xs uppercase tracking-[0.25em] py-3.5 transition-colors duration-300"
                        >
                            {saving ? "Menyimpan..." : "Simpan"}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>
            <Dialog open={!!sessionDialog} onOpenChange={(open) => { if (!open) setSessionDialog(null); }}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="time-record-session-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase tracking-wide text-xl">Edit Sesi Pencatatan</DialogTitle>
                        <DialogDescription className="sr-only">Ubah tanggal, judul kegiatan, nomor lomba, waktu semua atlet, dan tambahkan atlet lain ke sesi ini.</DialogDescription>
                    </DialogHeader>
                    {sessionDialog && (
                        <form onSubmit={saveSession} className="space-y-5 mt-2" data-testid="time-record-session-form">
                            <div className="grid md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">1. Tanggal Pencatatan</label>
                                    <input
                                        type="date"
                                        required
                                        value={sessionForm.record_date}
                                        onChange={(e) => setSessionForm({ ...sessionForm, record_date: e.target.value })}
                                        data-testid="session-field-date"
                                        className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">2. Nomor Lomba</label>
                                    <select
                                        value={sessionForm.race_number}
                                        onChange={(e) => setSessionForm({ ...sessionForm, race_number: e.target.value })}
                                        data-testid="session-field-race-number"
                                        className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                    >
                                        <option value="">Tanpa nomor lomba</option>
                                        {raceOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">3. Judul Kegiatan</label>
                                    <input
                                        type="text"
                                        required
                                        value={sessionForm.event_number}
                                        onChange={(e) => setSessionForm({ ...sessionForm, event_number: e.target.value })}
                                        data-testid="session-field-event"
                                        className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                    />
                                </div>
                            </div>

                            <div className="border border-racing-line" data-testid="session-athlete-rows">
                                <div className="grid md:grid-cols-[1fr_180px_1fr] gap-3 px-4 py-3 border-b border-racing-line bg-racing-surface text-[10px] uppercase tracking-[0.2em] text-racing-muted">
                                    <span>Atlet Tercatat di Sesi Ini</span>
                                    <span>Waktu</span>
                                    <span>Catatan Opsional</span>
                                </div>
                                {sessionRowList.map((row, index) => (
                                    <div
                                        key={row.athleteId}
                                        className="grid md:grid-cols-[1fr_180px_1fr] gap-3 items-center px-4 py-3 border-b border-racing-line last:border-0"
                                        data-testid={`session-row-${index}`}
                                    >
                                        <div>
                                            <p className="font-medium text-sm text-neutral-900">{row.name}</p>
                                            <p className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">{row.class_name || "—"}</p>
                                        </div>
                                        <input
                                            type="text"
                                            value={row.time_text}
                                            onChange={(e) => updateSessionRow(row.athleteId, "time_text", e.target.value)}
                                            placeholder="00:45.32"
                                            data-testid={`session-time-${index}`}
                                            className="bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red"
                                        />
                                        <input
                                            type="text"
                                            value={row.note}
                                            onChange={(e) => updateSessionRow(row.athleteId, "note", e.target.value)}
                                            placeholder="Catatan lintasan/cuaca"
                                            data-testid={`session-note-${index}`}
                                            className="bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red"
                                        />
                                    </div>
                                ))}
                            </div>

                            <div className="border border-racing-gold bg-racing-gold/5 p-4 space-y-4" data-testid="session-add-athlete-panel">
                                <div className="flex flex-wrap items-center gap-3">
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-racing-golddeep flex items-center gap-2"><Plus size={14} weight="bold" /> Tambah Atlet ke Sesi Ini</p>
                                    <select
                                        value={sessionAddClass}
                                        onChange={(e) => { setSessionAddClass(e.target.value); }}
                                        data-testid="session-add-class"
                                        className="bg-white border border-racing-line px-3 py-2 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                    >
                                        <option value="">Pilih kelas atlet...</option>
                                        {classOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                                    </select>
                                </div>
                                {sessionAddClass && sessionAddAthletes.length === 0 && (
                                    <p className="text-xs text-racing-muted" data-testid="session-add-empty">Semua atlet kelas ini sudah tercatat di sesi ini.</p>
                                )}
                                {sessionAddAthletes.length > 0 && (
                                    <div className="border border-racing-line bg-white" data-testid="session-add-rows">
                                        <div className="grid md:grid-cols-[1fr_180px_1fr] gap-3 px-4 py-2.5 border-b border-racing-line bg-racing-surface text-[10px] uppercase tracking-[0.2em] text-racing-muted">
                                            <span>Atlet {sessionAddClass}</span>
                                            <span>Waktu</span>
                                            <span>Catatan Opsional</span>
                                        </div>
                                        {sessionAddAthletes.map((athlete, index) => (
                                            <div key={athlete.id} className="grid md:grid-cols-[1fr_180px_1fr] gap-3 items-center px-4 py-3 border-b border-racing-line last:border-0" data-testid={`session-add-row-${index}`}>
                                                <p className="font-medium text-sm text-neutral-900">{athlete.name}</p>
                                                <input
                                                    type="text"
                                                    value={sessionAddEntries[athlete.id]?.time_text || ""}
                                                    onChange={(e) => updateSessionAddEntry(athlete.id, "time_text", e.target.value)}
                                                    placeholder="00:45.32"
                                                    data-testid={`session-add-time-${index}`}
                                                    className="bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red"
                                                />
                                                <input
                                                    type="text"
                                                    value={sessionAddEntries[athlete.id]?.note || ""}
                                                    onChange={(e) => updateSessionAddEntry(athlete.id, "note", e.target.value)}
                                                    placeholder="Catatan lintasan/cuaca"
                                                    data-testid={`session-add-note-${index}`}
                                                    className="bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <p className="text-[10px] text-racing-muted" data-testid="session-add-hint">Pilih kelas, lalu isi waktu atlet yang ikut mencatat hari itu. Baris yang dikosongkan tidak ikut tersimpan.</p>
                            </div>

                            <button
                                type="submit"
                                disabled={saving}
                                data-testid="session-save-button"
                                className="w-full bg-racing-red hover:bg-racing-redhover disabled:opacity-50 text-white text-xs uppercase tracking-[0.25em] py-3.5 transition-colors duration-300"
                            >
                                {saving ? "Menyimpan..." : "Simpan Sesi"}
                            </button>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
